import { z } from 'zod';
import { BagPhotoRecord } from '../bag-photos';
import {
  CurrencyCode,
  GUEST_NOTE_MAX,
  ImageUrl,
  Instant,
  LocalTime,
  MinorUnits,
  MinorUnitsNonNegative,
  Ok,
  PageRequest,
  pageOf,
  ServiceDay,
  Uuid,
} from '../common';
import {
  BakeryQueueName,
  BakeryStorefrontMode,
  CheckoutRefusalCause,
  ClaimAction,
  ClaimPhase,
  DeliveryClosedOutcome,
  FulfillmentMode,
  OrderLifecycleWire,
  OrderPhase,
  OrderSummaryState,
  RejectionReasonCode,
  RetiredOrderPhase,
  ServicePauseReason,
  StorefrontMode,
} from '../enums';
import { FilterFacet } from '../facets';
import { defineEndpoint } from '../http';
import { InternalAlertKind } from '../internal-alert-kinds';
import {
  AllocationSnapshot,
  DeliveryJobSummary,
  OrderLine,
  PriceBreakdown,
  ProofOfDelivery,
} from '../models';
import { BAKERY_SERVICE_REASONS, BAKERY_SERVICE_STATUSES } from '../projections/counter-intake';
import { CounterReadinessSchema } from '../projections/counter-readiness';
import { orderStopped } from '../projections/order-phase';
import { SUPPORT_REASON_KINDS } from '../projections/support-case-table';
import { OperationsAlert } from './operations';

// ---------------------------------------------------------------------------
// Admin dashboard (PRD OPS-001, RTE-006)
// ---------------------------------------------------------------------------

export const DashboardRow = z.object({
  orderId: Uuid,
  shortCode: z.string().max(10),
  serviceDay: ServiceDay,
  /** GST-016: an order for today is IMMEDIATE; the row says so instead of a date. */
  fulfillmentMode: FulfillmentMode,
  lifecycle: OrderLifecycleWire,
  summary: OrderSummaryState,
  storefrontMode: StorefrontMode,
  /** ALC-010's fact: a guest-chosen shop is never silently swapped. With
   *  `status` and `storefrontMode`, what `reassignEligible` (projections) asks. */
  guestSelectedBakery: z.boolean(),
  bakeryLocationId: Uuid.nullable(),
  bakeryName: z.string().max(160).nullable(),
  propertyReference: z.string().max(80),
  promisedWindowStart: Instant,
  promisedWindowEnd: Instant,
  estimatedArrival: Instant.nullable(),
  /** Minutes the provider ETA exceeds the promised window; drives the sort (RTE-006). */
  latenessRiskMinutes: z.int(),
  latenessFlagged: z.boolean(),
  /**
   * The minutes past the window at which this row is LATE: the same setting
   * the lateness alert fires on (`LATENESS_ALERT_THRESHOLD_MINUTES`), so the
   * board and the alert can never name a different moment (founder, D5 of
   * 2026-10-05). On the row rather than the page because every screen that
   * bands a row - the board, its counters, the order record - reads the row.
   */
  latenessThresholdMinutes: z.int().positive(),
  /**
   * When the bag reached the guest, or null. The order's own fact (the
   * delivery region's), as `InternalOrderRow.deliveredAt` is: a delivered
   * order's band on the board is judged on WHEN it arrived against its window,
   * as the order record's timing table judges it, never "On track" because
   * the clock stopped mattering.
   */
  deliveredAt: Instant.nullable(),
  /**
   * When the courier search running on this order gives up (RTE-035, #1758),
   * or null when none is running: the instant the order record's banner
   * prints, read off the same derivation (`courierSearchUnderway`). A Ready
   * order whose courier came to nothing says "No courier yet, searching
   * until HH:MM" beside its state (#2004).
   */
  courierSearchDeadline: Instant.nullable(),
  delivery: DeliveryJobSummary.nullable(),
  /**
   * True when this order is waiting on an OPERATOR rather than on the platform.
   *
   * The same boolean `InternalOrderRow` carries and the same derivation
   * (`orderNeedsOperator` plus an open incident), on this row because the two
   * surfaces that read it - the admin dashboard and the order's own detail
   * screen - had no way to ask. Both drew `Badge tone="neutral"` on every
   * order, so 9B388H sat on the dashboard as a grey chip reading "Aucune
   * boulangerie trouvee" with the guest's EUR 83.68 still held and nothing on
   * either screen saying a human was owed. The Orders list has toned its badge
   * from this since it shipped; the fact was simply absent here.
   *
   * Never recomputed on a screen from `summary`: a summary is one lossy word
   * and the moment something DECIDES with it, it is `OrderStatus` again.
   */
  needsAction: z.boolean(),
  openIncidents: z.int().nonnegative(),
  /**
   * WHY the bakery refused, AS the closed code the tablet chose from (BAK-003,
   * finding F-38) - an operator screen localizes it exactly as the tablet
   * already does. The baker's free-text elaboration rides separately on
   * `rejectionNote`; both are null unless the order is REJECTED.
   */
  rejectionReasonCode: RejectionReasonCode.nullable(),
  rejectionNote: z.string().max(500).nullable(),
});
export type DashboardRow = z.infer<typeof DashboardRow>;

// ---------------------------------------------------------------------------
// Orders (PRD OPS-002)
// ---------------------------------------------------------------------------

/**
 * One row of the operations Orders list.
 *
 * Deliberately NOT `DashboardRow` with money bolted on. The two answer different
 * questions: the dashboard is one service morning ranked by lateness risk, and
 * carries only what a reader triaging that morning needs; this is every order
 * still in flight, whatever day it belongs to, with the commercial facts and the
 * parties - which is what an operator answering "who is this, what did they pay,
 * where is it going" needs and the board does not.
 *
 * What the two MUST agree on is the classification, and they do: `status` is the
 * order's own, `latenessRiskMinutes` and `latenessFlagged` come from the same
 * computation the board sorts on, and `needsAction` is derived from the shared
 * `DeliveryPhase` model rather than from a second list of statuses kept here.
 */
export const InternalOrderRow = z.object({
  orderId: Uuid,
  shortCode: z.string().max(10),
  serviceDay: ServiceDay,
  /** GST-016: an order for today is IMMEDIATE; the row says so instead of a date. */
  fulfillmentMode: FulfillmentMode,
  /**
   * The stay this order is one morning of, or null when it is a single morning.
   *
   * A multi-day order is not one row with several dates: checkout places ONE
   * order per morning (section 4.3), each with its own window, its own bakery,
   * its own status and its own price - which is why the list has always shown
   * them as separate rows and why the total beside each one is that morning's
   * total, never a stay's. What the list could NOT say is that a row belongs to
   * a stay at all, so five rows a guest experiences as one booking read as five
   * unrelated orders. This is the fact that says so; it is the schedule's own
   * id, so two rows of the same stay carry the same value.
   */
  stayId: Uuid.nullable(),
  /** When the guest placed it - the "date/time" column, distinct from the window. */
  placedAt: Instant,
  promisedWindowStart: Instant,
  promisedWindowEnd: Instant,
  /**
   * When it actually arrived, or null while it has not. The promised window is
   * what was owed; this is what happened, and the two are different facts - a
   * row reading DELIVERED under a 07:15-07:45 window says nothing about whether
   * it made it. Taken from the order itself rather than from `delivery`, which
   * is null for any order that never reached a provider.
   */
  deliveredAt: Instant.nullable(),
  lifecycle: OrderLifecycleWire,
  summary: OrderSummaryState,
  /**
   * The mode the order was placed through, and whether the guest chose the
   * shop (ALC-010). With `status`, exactly what `reassignEligible` in
   * `projections/reassign-eligibility.ts` asks — the row menu disables the
   * Reassign entry from the same table the server refuses by, so enabled
   * equals legal (OPS-002, finding F-34).
   */
  storefrontMode: StorefrontMode,
  guestSelectedBakery: z.boolean(),
  /** The source bakery. Null while the order is still unallocated. */
  bakeryLocationId: Uuid.nullable(),
  bakeryName: z.string().max(160).nullable(),
  /**
   * The apartment the order goes to. The destination cell links to its owner's
   * page opened on this apartment's row (`apartmentHref`), because Internal has
   * no apartment page of its own.
   */
  propertyId: Uuid,
  /** The destination: the apartment, its address, and the owner it belongs to. */
  propertyReference: z.string().max(80),
  deliveryAddress: z.string().max(400),
  partnerOrgId: Uuid,
  partnerOrgName: z.string().max(160).nullable(),
  /** How many items are on the order, and what the guest was charged for them. */
  itemCount: z.int().nonnegative(),
  guestTotal: MinorUnitsNonNegative,
  currency: CurrencyCode,
  /**
   * True when this order is waiting on an operator rather than on the platform -
   * a failed payment, a refused allocation, a dead courier, an open incident.
   * One boolean rather than "work it out from the status", because every surface
   * that has worked it out separately has eventually disagreed with the others.
   */
  needsAction: z.boolean(),
  openIncidents: z.int().nonnegative(),
  latenessRiskMinutes: z.int(),
  latenessFlagged: z.boolean(),
});
export type InternalOrderRow = z.infer<typeof InternalOrderRow>;

/**
 * The three views of the orders table (OPS-002), in the order the filter offers
 * them: the working set, the subset that needs somebody, and everything.
 *
 * Named because the screen has to draw one option per member, and a hand-kept
 * copy of a wire enum is a select that offers a value the server refuses (or
 * silently stops offering one it accepts). The ORDER is part of what is
 * published, exactly as `ActivityKind`'s already is: it is what the reader
 * scans, narrowest first.
 */
export const OrderListScope = z.enum(['ACTIVE', 'NEEDS_ACTION', 'ALL']);
export type OrderListScope = z.infer<typeof OrderListScope>;

/**
 * What the Orders list is ordered BY, decided on the server (OPS-002).
 *
 * On the server because a list this long is paged: the endpoint holds every
 * order the scope names and hands back one page of them, so whatever orders
 * that page decides WHICH orders a reader can see at all - not merely the
 * sequence they appear in. Sorting the fetched page in the browser therefore
 * answers correctly about the wrong rows. On the demo world the ALL scope holds
 * 817 orders, its first page spanned 1 August to 3 September, and "newest
 * first" in the browser put 3 September on top while the four newest days sat
 * in pages nobody had asked for (2026-09-07).
 *
 * `PRIORITY` is the operational triage this list used to be ordered by and
 * could not be ordered by any other way: what needs an operator, then how late
 * it is, then the morning it was promised for. It stays, because it is the
 * right order for somebody working the morning - it is simply no longer the
 * only one, and no longer the default. The default is the one a reader expects
 * of a list of orders: when it was placed, newest first.
 */
export const OrderListSort = z.enum([
  /** When the guest placed it - the default, newest first. */
  'PLACED',
  /** The morning it is FOR, then the promised window inside that morning. */
  'SERVICE_DAY',
  'BAKERY',
  /** The owner, then the apartment - the order the destination column's first line reads in. */
  'DESTINATION',
  /** The owner alone. Kept for API callers; the list has no client column of its own any more. */
  'CLIENT',
  'TOTAL',
  /**
   * The order machine's own state (`lifecycle.order`) - what the State column
   * prints since 2026-09-14, rather than the one-word summary - ordered by the
   * order `OrderPhase` declares its states in, not by the translated word, so
   * every language reads one ordering.
   */
  'STATE',
  /** What needs an operator first, then lateness, then the promised morning. */
  'PRIORITY',
  /** Immediate, then pre-order, then multi-day pre-order (`OrderListType`'s order). */
  'TYPE',
]);
export type OrderListSort = z.infer<typeof OrderListSort>;

/**
 * Which way. Stated by the caller rather than defaulted per column, so the
 * cursor and the arrow in the heading can never disagree about it.
 */
export const SortDirection = z.enum(['ASC', 'DESC']);
export type SortDirection = z.infer<typeof SortDirection>;

/**
 * The three kinds of order the Orders list tells apart (OPS-002), printed as
 * I, P and MP in its Type column: an immediate order, a pre-order for one
 * morning, and one morning of a multi-day pre-order (a stay). Derived from the
 * row's own `fulfillmentMode` and `stayId` by `orderTypeOf`, never stored.
 */
export const OrderListType = z.enum(['IMMEDIATE', 'PRE_ORDER', 'MULTI_DAY']);
export type OrderListType = z.infer<typeof OrderListType>;

/** The one derivation of a row's type - the column, the filter and the counts all read it. */
export function orderTypeOf(
  row: Pick<InternalOrderRow, 'fulfillmentMode' | 'stayId'>,
): OrderListType {
  if (row.stayId !== null) return 'MULTI_DAY';
  return row.fulfillmentMode === 'IMMEDIATE' ? 'IMMEDIATE' : 'PRE_ORDER';
}

/**
 * Whether an Orders row is SETTLED: the order has ended (cancelled or closed,
 * section 4.2) and nobody needs to act on it.
 *
 * The one rule behind two things a reader sees together, so they cannot
 * disagree: the row's grey ground, and the list's order - every live row
 * before every settled one, whichever heading orders the list and in both
 * directions (founder, 2026-09-14). An ended order still waiting on an
 * operator - a refund, an issue - is not settled; it is drawn red and sorts
 * with the live ones.
 */
export function orderRowSettled(row: Pick<InternalOrderRow, 'lifecycle' | 'needsAction'>): boolean {
  return !row.needsAction && orderStopped(row.lifecycle.order);
}

/** How many listed orders carry one value of a filter: the platform's one facet shape. */
export const OrderListFacet = FilterFacet;
export type OrderListFacet = FilterFacet;

/**
 * A shop facet carries the shop's name ("House - Shop"), because the screen
 * holds no directory of every shop: it named them from the Admin dashboard's
 * board, which lists only the shops trading today, and printed the raw id of
 * every other shop the list holds orders for.
 */
export const OrderListShopFacet = FilterFacet.extend({ label: z.string().max(200) });
export type OrderListShopFacet = z.infer<typeof OrderListShopFacet>;

export const listOrders = defineEndpoint({
  method: 'GET',
  path: '/internal/orders',
  mount: 'internal',
  summary: 'Every order, with its parties, money and required action (OPS-002)',
  query: PageRequest.extend({
    /**
     * Omitted means ALL: every order the platform holds (founder, 2026-09-14).
     * The working set - ACTIVE - and the subset that needs somebody are the
     * narrower views a reader opts into.
     */
    scope: OrderListScope.default('ALL'),
    serviceDay: ServiceDay.optional(),
    /** Any of these shops; omitted or empty means every shop. */
    bakeryLocationId: z.array(Uuid).max(200).optional(),
    partnerOrgId: Uuid.optional(),
    /** Any of these states; omitted or empty means every state. */
    summary: z.array(OrderSummaryState).max(OrderSummaryState.options.length).optional(),
    /** Any of these kinds of order; omitted or empty means every kind. */
    orderType: z.array(OrderListType).max(OrderListType.options.length).optional(),
    /**
     * Any of these states of the order machine (section 4.2) - the state the
     * State column prints; omitted or empty means every state.
     *
     * IT ACCEPTS THE FOUR RETIRED NAMES FOR ONE RELEASE, and ignores them, for
     * the reason a renamed request value always stays for one: this is
     * a REQUEST enum and a request enum has no deploy-ordering protection. The
     * worker deploys before internal-admin, so for a few minutes an operator's
     * open tab sends the selection ITS bundle rendered - and the selection is
     * kept per history entry, so it survives the deploy, as does any incident
     * link somebody pasted with `orderPhase=REFUSED` in it. A narrowed-only
     * enum answers that with a 400 and an empty Orders list. The new bundle
     * already drops non-live values on its own side (`livePhases` in
     * screens/Orders.tsx); this is the same decision on the server, and the
     * retired arm goes in the release after this one.
     */
    orderPhase: z
      .array(z.union([OrderPhase, RetiredOrderPhase]))
      .max(OrderPhase.options.length + RetiredOrderPhase.options.length)
      .optional(),
    /**
     * One lane of a counter's board (BAK-002), counted exactly as the Active
     * bakeries table counts it: the same projection, the same shop attribution,
     * and for Scheduled the same mornings out to the booking horizon. With a
     * lane the day defaults to the platform's own today and the scope is not
     * applied, so a count and the list it links to are one figure (OPS-035).
     */
    bakeryQueue: BakeryQueueName.optional(),
    /** Matches the short code, the apartment reference or the address. */
    search: z.string().max(120).optional(),
    /**
     * Omitted means the newest orders first. The pair is what the cursor is
     * built from, so a page that changes either starts again from the top -
     * a cursor minted under one order names a position that does not exist
     * under another.
     */
    sort: OrderListSort.default('PLACED'),
    direction: SortDirection.default('DESC'),
  }),
  response: z.object({
    serverTime: Instant,
    rows: pageOf(InternalOrderRow),
    /**
     * How many orders the scope, the filters and the search actually match -
     * the whole of it, not this page of it.
     *
     * A cursor page carries no total by design (packages/ui `page-slice.ts`),
     * and for most lists that is right: nothing counted them and a figure
     * nobody can stand behind is worse than none. This handler is the
     * exception, and it costs nothing here - it builds every matching row
     * before it slices one page out, so the count is already in its hand. It
     * exists because the honest alternative was a pager reading `100+` over
     * 817 orders, which answers "how many are there" with "more than you can
     * see" (founder, 2026-09-07).
     */
    total: z.int().nonnegative(),
    /**
     * Which values each multi-select filter can offer, and how many orders
     * carry each one (founder, 2026-09-14: an option no row carries is not
     * offered). Counted over the whole list the scope, the day, the lane and
     * the search match - never over one page of it - and each filter is
     * counted with the OTHER two applied and its own ignored, so ticking one
     * status does not hide the statuses the reader is still choosing between.
     * A value no order carries is absent rather than zero.
     */
    facets: z.object({
      summary: z.array(OrderListFacet),
      bakery: z.array(OrderListShopFacet),
      orderType: z.array(OrderListFacet),
      orderPhase: z.array(OrderListFacet),
    }),
  }),
});

/**
 * The platform's own "now" and the service day it falls on in Europe/Paris.
 *
 * Every dated screen in the operations portal defaults its date from THIS,
 * never from the browser's clock: outside production the platform runs on the
 * rehearsal clock (TST-003), and a board that opened on the browser's date
 * rendered an empty service morning while printing the server's time on the
 * same line.
 */
export const PlatformTime = z.object({ now: Instant, serviceDay: ServiceDay });

export const getPlatformTime = defineEndpoint({
  method: 'GET',
  path: '/internal/time',
  mount: 'internal',
  summary: 'The platform clock and its current Paris service day (OPS-001, TST-003)',
  response: PlatformTime,
});

/**
 * One BAKERY SHOP's readiness for one morning (OPS-001, BAK-002, BAK-020).
 *
 * A SHOP, not a house. A house is the organization that owns the shops; it has
 * no counter, no tablet, no opening time and no morning to be ready for. This
 * row read the HOUSE's name for every counter until 2026-08-24, so a chain's
 * three shops arrived as three identical lines and the OPS-011 alert for a dark
 * tablet named a company rather than the address an operator has to telephone.
 * `name` is the shop's own (BAK-028) and `houseName` is the owner beside it.
 *
 * The counts ARE the counter's board (BAK-002), lane for lane, through the one
 * `bakeryQueueOf` projection the tablet's own queue is built from - so an
 * operator reading "two to accept" and the baker looking at the New lane are
 * reading one derivation, never two ladders over the same OrderStatus.
 */
/** Physical device connectivity; independent of the shop's service pause. */
export const BakeryReadinessDevice = z.object({
  id: Uuid,
  label: z.string(),
  active: z.boolean(),
  lastSeenAt: Instant.nullable(),
  connectivity: z.enum(['READY', 'STANDBY', 'UNREACHABLE', 'INACTIVE']),
});
export type BakeryReadinessDevice = z.infer<typeof BakeryReadinessDevice>;

export const BakeryReadiness = z.object({
  locationId: Uuid,
  /**
   * The house that owns the shop. On the wire beside its name because the
   * reassign pickers are built from this array, and a guest-chosen mode B
   * order may only be offered its OWN house's other shops (ALC-010) - which a
   * name cannot decide, two houses being free to be called the same thing.
   */
  houseId: Uuid,
  /** The SHOP's own name - "The French Bastards Oberkampf" (BAK-028). */
  name: z.string().max(160),
  /** The house that owns it. Equal to `name` for a single-counter house. */
  houseName: z.string().max(160),
  /** OPS-013 - the house's exclusive mode, which its shops trade under. */
  storefrontMode: BakeryStorefrontMode,
  /**
   * False when this counter is suspended - its own switch, or its house's
   * (OPS-003). A suspended shop wins no allocation and has no morning, so it
   * is not listed under readiness; it stays in the payload because the board's
   * bakery filter is built from this array and an operator must still be able
   * to look up the orders a shop took before it was switched off.
   */
  active: z.boolean(),
  /**
   * THE word about this counter (OPS-035), derived by `counterReadinessOf` in
   * `../projections/counter-readiness` and by nothing on a screen.
   *
   * `tabletHealthy` and `tabletLastSeenAt` stay beside it: they are two of the
   * facts it is derived FROM, an older client parses them, and the minutes-
   * since-last-beat line is read off the second. Nothing derives a verdict from
   * them any more - that is what this field is.
   */
  readiness: CounterReadinessSchema,
  tabletLastSeenAt: Instant.nullable(),
  tabletHealthy: z.boolean(),
  /** BAK-037 - how many devices are paired here. Zero is onboarding, not an outage. */
  deviceCount: z.int().nonnegative(),
  /** Paired devices only, including inactive records. Absent on older servers. */
  devices: z.array(BakeryReadinessDevice).optional(),
  /** The counter's board for this morning, lane for lane (BAK-002). */
  board: z.object({
    /** Waiting for somebody at the counter to answer (BAK-003). */
    NEW: z.int().nonnegative(),
    /** Planifiees - booked, not started; later mornings included. */
    PREPARE_LATER: z.int().nonnegative(),
    /** In the oven. */
    PREPARE_NOW: z.int().nonnegative(),
    /** Bagged, waiting for the courier. */
    READY: z.int().nonnegative(),
    /** Picked up from the counter and gone. */
    COLLECTED: z.int().nonnegative(),
    /** Something is stuck and somebody has to act. */
    ISSUES: z.int().nonnegative(),
  }),
  /**
   * How many of this morning's orders the COUNTER changed for a stock-out
   * (BAK-019) - a line replaced, reduced or dropped. Not a lane: an amended
   * order sits in whichever lane its status puts it in and is counted here as
   * well. A refund the operations portal issued is not counted - that is the
   * platform's action, not the baker's.
   */
  amendedOrders: z.int().nonnegative(),
  earliestReadyLocalTime: LocalTime,
  /** Demo executor, independent of shop hours and pauses. Never true in production. */
  robobaker: z.boolean().default(false),
  /** Today's shop service spans, separate from the device heartbeat. */
  serviceHours: z
    .array(z.object({ earliest: LocalTime, latest: LocalTime.nullable() }))
    .default([]),
  closure: z.object({ from: ServiceDay, to: ServiceDay }).nullable().default(null),
  paused: z.boolean(),
  /**
   * PRD BAK-038 - WHY it is paused, so an operator can tell a baker who
   * chose to stop from a counter the platform stopped because nobody was
   * answering it. Null when the counter is not paused.
   */
  pauseReason: ServicePauseReason.nullable(),
  /**
   * OPS-035 - the status column's one word, derived on the SERVER from the
   * counter's intake and readiness (`bakeryServiceStatusOf`, K2). The fields
   * above are what it is read from, not a second verdict for a screen to make.
   */
  serviceStatus: z.enum(BAKERY_SERVICE_STATUSES),
  /**
   * #2004 - why that word is Paused or Suspended, as text beside the pill: the
   * baker, the kill switch, two unanswered orders, an unfinished payout setup
   * or an unstated VAT status (`bakeryServiceReasonOf`). Null for every other
   * word. Defaulted for an older server, which sent none.
   */
  serviceReason: z.enum(BAKERY_SERVICE_REASONS).nullable().default(null),
});
export type BakeryReadiness = z.infer<typeof BakeryReadiness>;

export const getDashboard = defineEndpoint({
  method: 'GET',
  path: '/internal/dashboard',
  mount: 'internal',
  summary: 'Live orders, bakery readiness and provider jobs sorted by lateness risk (OPS-001)',
  query: PageRequest.extend({
    serviceDay: ServiceDay.optional(),
    /** Any of these shops; omitted or empty means every shop. */
    bakeryLocationId: z.array(Uuid).max(200).optional(),
    /** Any of these states; omitted or empty means every state. */
    summary: z.array(OrderSummaryState).max(OrderSummaryState.options.length).optional(),
    flaggedOnly: z.boolean().optional(),
  }),
  response: z.object({
    serverTime: Instant,
    rows: pageOf(DashboardRow),
    bakeryReadiness: z.array(BakeryReadiness),
    /**
     * Which states and which shops the board's two multi-select filters can
     * offer, and how many of the day's orders carry each (founder, 2026-09-14:
     * an option no row carries is not offered). Counted over the whole service
     * day `flaggedOnly` leaves - never over one page of the ranking - and each
     * filter with the OTHER one applied and its own ignored (`facetedFilter`).
     */
    facets: z.object({
      summary: z.array(FilterFacet),
      bakery: z.array(FilterFacet),
    }),
  }),
});

/**
 * The dashboard's readiness table on its own (OPS-035): what Active bakeries,
 * Health and the Reassign pickers read, every 15 seconds on the first, without
 * the order board they never draw.
 */
export const getBakeryReadiness = defineEndpoint({
  method: 'GET',
  path: '/internal/bakery-readiness',
  mount: 'internal',
  summary: "Every shop's readiness row, as the admin dashboard lists it (OPS-035)",
  query: z.object({ serviceDay: ServiceDay.optional() }),
  response: z.object({
    serverTime: Instant,
    bakeryReadiness: z.array(BakeryReadiness),
  }),
});

/**
 * One message of an order's SupportConversation (PRD section 7, SUP-006,
 * GST-039): the guest's report and every operations reply, in order. Derived
 * from the order's append-only event history - the conversation IS those
 * events read back, so it survives restarts and needs no second store.
 */
export const SupportMessage = z.object({
  at: Instant,
  author: z.enum(['GUEST', 'OPERATIONS']),
  text: z.string().max(2000),
  /** The support ticket the guest's report opened (SUP-006); null on a reply. */
  ticketRef: z.string().max(80).nullable(),
  /** How many order lines the guest flagged; null on a reply. */
  itemsFlagged: z.int().nonnegative().nullable(),
});
export type SupportMessage = z.infer<typeof SupportMessage>;

/**
 * The provider claim, on its own clock (plan 1.4).
 *
 * It is on the order detail rather than on a register of its own because it is
 * the last thing that can still be true about an order everything else has
 * finished with: the guest was refunded when it opened, so no other surface
 * has a reason to show it, and an operator asking "did we ever get that money
 * back from Uber" is asking about one order.
 */
export const ClaimView = z.object({
  state: ClaimPhase,
  at: Instant.nullable(),
  amount: MinorUnitsNonNegative.nullable(),
  reference: z.string().max(120).nullable(),
  reason: z.string().max(500).nullable(),
  /** Empty unless the claim may move right now - the same rule as `decisions`. */
  actions: z.array(ClaimAction),
});
export type ClaimView = z.infer<typeof ClaimView>;

/**
 * WHICH register named an id, so a screen can open the record behind it.
 *
 * The name alone made a payload readable; it did not make it reachable. An
 * operator reading "offered to Le Grenier a Pain - Rue des Martyrs" still had
 * to go and find that shop through the Bakeries list, and the apartment an
 * order was placed from could not be opened at all - a uuid under a label.
 * Routes are the CLIENT's business, so the wire says what the record IS and
 * each portal maps that to its own path; the alternative is the API composing
 * `/organizations/bakeries/shops/...`, which is an internal-admin URL travelling
 * through a contract four surfaces read.
 */
export const PayloadSubject = z.enum([
  'BAKERY_SHOP',
  'BAKERY_HOUSE',
  'PARTNER',
  'APARTMENT',
  'ORDER',
]);
export type PayloadSubject = z.infer<typeof PayloadSubject>;

/**
 * One named id: what it is called, what kind of record it is, and - when the
 * record has no page of its own - who owns it.
 *
 * `ownerId` exists for the apartment and for nothing else so far. Internal has
 * no apartment screen: an apartment is a row inside its conciergerie's page, so
 * reaching one means naming the organization that holds it. A shop, a house, a
 * partner and an order each have a page keyed on their own id and carry null.
 */
export const PayloadName = z.object({
  name: z.string().max(200),
  subject: PayloadSubject,
  ownerId: Uuid.nullable(),
});
export type PayloadName = z.infer<typeof PayloadName>;

/**
 * The ids a page of payloads mentions, named (OPS-002, OPS-027).
 *
 * A directory rather than a rewritten payload, for one reason: `order_events`
 * and the audit log are APPEND-ONLY, so the row an operator is reading was
 * written months ago and the name it should print is whatever the shop is
 * called today. The endpoint resolves it on the way out - exactly as an audit
 * row's `actorName` is resolved beside its immutable `actor` - and the screen
 * looks each id up here before printing it.
 *
 * It exists because a timeline that showed three houses refusing an order
 * showed them as `8ef3ce4b-...` under a label reading "Boulangerie", three
 * times, while the order's own header two cards above said "Le Grenier a Pain".
 * An id an operator cannot resolve is not information.
 *
 * An id that is ABSENT here is one no register could name; the screen then
 * prints it short, which is what it did for all of them before.
 */
export const PayloadNames = z.record(Uuid, PayloadName);
export type PayloadNames = z.infer<typeof PayloadNames>;

/**
 * How loudly the feed renders an item (OPS-027), LOUDEST FIRST - an operator
 * scanning the severity filter is looking for the bad ones, and the order this
 * enum is written in is the order that select is drawn in.
 */
export const ActivitySeverity = z.enum(['DANGER', 'WARNING', 'INFO']);
export type ActivitySeverity = z.infer<typeof ActivitySeverity>;

/**
 * What the guest actually paid, on the line that says the money moved
 * (founder, 2026-09-07: "for payement we need to know the amount paid by the
 * customer").
 *
 * `PAYMENT_CAPTURED` carries no payload at all - the event IS the fact, and
 * the machine needs nothing else to move the payment axis - so an operator
 * reading the order's history saw "Paiement capture, par Plateforme" and had to
 * go and reconcile it against a card further up the page. Every figure here is
 * DERIVED at read time from the placement snapshot and the one money view
 * (`orderMoneyView`), never written into the append-only row: an order captured
 * in August answers this as completely as one captured this morning.
 *
 * `stay` is the half a single order cannot answer. A guest booking five
 * mornings is charged per morning (section 4.3) - five orders, five captures -
 * and until now nothing on this screen said the other four existed, so a
 * complaint about "my booking" was read against one fifth of it.
 */
export const OrderPaymentSummary = z.object({
  currency: CurrencyCode,
  /** What the card was charged for THIS morning, VAT included. */
  total: MinorUnitsNonNegative,
  /** GST-016: a bag for now, or a morning booked ahead. */
  fulfillmentMode: FulfillmentMode,
  /** The three receipt rows behind the total, VAT included (GST-022). */
  merchandise: MinorUnitsNonNegative,
  deliveryFee: MinorUnitsNonNegative,
  serviceFee: MinorUnitsNonNegative,
  /** Units, not lines: three croissants and a baguette is four. */
  itemCount: z.int().nonnegative(),
  items: z.array(
    z.object({
      name: z.string().max(120),
      quantity: z.int().positive(),
      lineTotal: MinorUnitsNonNegative,
    }),
  ),
  /**
   * The stay this morning is one of, and every order in it - null on a single
   * morning. THIS order is in the list too, so the count reads as the guest
   * experienced the booking rather than as "four others".
   */
  stay: z
    .object({
      stayId: Uuid,
      orders: z.array(
        z.object({
          orderId: Uuid,
          shortCode: z.string().max(10),
          serviceDay: ServiceDay,
          total: MinorUnitsNonNegative,
        }),
      ),
    })
    .nullable(),
});
export type OrderPaymentSummary = z.infer<typeof OrderPaymentSummary>;

/**
 * Who this order is FOR (founder, 2026-09-07: "I need to see which appartment
 * has initiated the order, including the name of the partner / conciergerie").
 *
 * The card printed `propertyReference` - "BRETEUIL-3B" - and stopped. That is
 * the code on the sticker beside the door, which is exactly what an operator
 * cannot phone: whose apartment it is, at what address, and which conciergerie
 * to call are three facts the screen did not have. The bakery is not repeated
 * here; `row.bakeryLocationId` and `row.bakeryName` already carry it.
 */
/**
 * What the gateway reported about an order's payment, as `payments` stores it:
 * the order page's Stripe group. `paymentRef` is the payment (`pi_...`), the id
 * the Stripe dashboard's payment page is named by, so the id a screen prints is
 * the id the page it opens shows. `processingFeeMinor` is the gateway's own
 * figure, null when only an estimate is known.
 */
export const stripePaymentSchema = z.object({
  paymentRef: z.string().nullable(),
  cardBrand: z.string().nullable(),
  processingFeeMinor: MinorUnitsNonNegative.nullable(),
  billingCountry: z.string().nullable(),
  capturedAt: Instant.nullable(),
});
export type StripePaymentWire = z.infer<typeof stripePaymentSchema>;

export const OrderParties = z.object({
  apartment: z.object({
    propertyId: Uuid,
    /**
     * The apartment's own name, which IS the reference an operator reads off
     * the board - `DashboardRow.propertyReference` is this same string. Said
     * once here rather than twice under two labels.
     */
    name: z.string().max(200),
    /** One line: street, postal code, city. Empty for an apartment since removed. */
    address: z.string().max(400),
  }),
  /**
   * The conciergerie or owner that holds the apartment. Null only for an
   * apartment whose organization no register could name.
   */
  partner: z.object({ organizationId: Uuid, name: z.string().max(200) }).nullable(),
  /**
   * NTF-040: the guest, so an operator can reach them from the order - by
   * WhatsApp, by phone or by SMS. The phone is plain text, not `PhoneE164`: an
   * erased order holds `redacted` there (GST-018). Optional on the wire so an
   * Internal bundle newer than the worker it reads still opens the order.
   */
  guest: z
    .object({
      /** The whole name as the guest typed it - checkout stores one name field. */
      firstName: z.string().max(200),
      phone: z.string().max(40),
      /** Null when the guest gave none at checkout. */
      email: z.string().max(320).nullable(),
      /** They asked at checkout for order updates on WhatsApp. */
      whatsappUpdates: z.boolean(),
      /** Their note to the house (GST-163); null when they left none. */
      note: z.string().max(GUEST_NOTE_MAX).nullable(),
    })
    .optional(),
});
export type OrderParties = z.infer<typeof OrderParties>;

/**
 * Which region an `order_events` row moved (PRD 4.2).
 *
 * The four regions, plus the three names only HISTORY holds: rows written
 * before the four-region machine say `assignment`, `bag` or `lifecycle`, and
 * `order_events` is append-only, so a reader has to be able to name them for
 * ever. The wire twin of `EventRegionName` in `@croissants/domain`.
 */
export const EventAxisName = z.enum([
  'payment',
  'order',
  'delivery',
  'claim',
  'assignment',
  'bag',
  'lifecycle',
]);
export type EventAxisName = z.infer<typeof EventAxisName>;

/** Body text is fetched only when opening an activity row. Credentials are redacted at capture. */
export const ProviderExchangeMessage = z.object({
  method: z.string().optional(),
  url: z.string().optional(),
  headers: z.record(z.string(), z.string()),
  body: z.string().nullable(),
});
export const ProviderExchangeSummary = z.object({
  id: Uuid,
  source: z.enum(['TRACE', 'EVENT']),
  providerName: z.string(),
  operationId: z.string().nullable(),
  operation: z.string(),
  direction: z.enum(['OUTBOUND', 'WEBHOOK', 'EVENT']),
  externalEventId: z.string().nullable(),
  jobRef: z.string().nullable(),
  quoteRef: z.string().nullable(),
  /**
   * The call as it was SENT - `POST` and `/v2/jobs` - which is how the order's
   * timeline names it. Read off the stored (already redacted) request; the
   * host and the query are left out. Null on a receipt, which nobody here
   * sent.
   */
  method: z.string().max(16).nullable(),
  path: z.string().max(200).nullable(),
  /**
   * `platformAt` is when the platform SENT the call, on the platform clock;
   * `durationMs` is how long the answer took, so the answer is the sum. The
   * timeline sorts a call by when it was sent.
   */
  startedAt: Instant,
  platformAt: Instant,
  durationMs: z.number().nullable(),
  status: z.number().int().nullable(),
  error: z.string().nullable(),
  processedAt: Instant.nullable(),
  processingError: z.string().nullable(),
  /**
   * WHAT the provider said, for a receipt: its own event type (`courier_update`,
   * `delivery_status`, Stuart's `package_delivered`), read off the
   * `provider_events` row the receipt was persisted as. A traced receipt's
   * `operation` is the constant `webhook` - nobody here called anything - so
   * without this the row could say a courier network spoke and not what about.
   * Null on an outbound call, and on a receipt that verified but was never
   * persisted as an event.
   */
  eventType: z.string().max(120).nullable(),
});
export type ProviderExchangeSummary = z.infer<typeof ProviderExchangeSummary>;
export const listOrderProviderExchanges = defineEndpoint({
  method: 'GET',
  path: '/internal/orders/:orderId/provider-exchanges',
  mount: 'internal',
  summary: 'Delivery requests, responses and webhook receipts for one order (OPS-002)',
  params: z.object({ orderId: Uuid }),
  response: z.object({ items: z.array(ProviderExchangeSummary) }),
});
export const getOrderProviderExchange = defineEndpoint({
  method: 'GET',
  path: '/internal/orders/:orderId/provider-exchanges/:exchangeId',
  mount: 'internal',
  summary: 'Full retained payloads for one order activity row (OPS-002)',
  params: z.object({ orderId: Uuid, exchangeId: Uuid }),
  query: z.object({ source: z.enum(['TRACE', 'EVENT']) }),
  response: z.object({
    request: ProviderExchangeMessage,
    response: ProviderExchangeMessage.nullable(),
    /** Historical events have no retained request headers or acknowledgement. */
    legacy: z.boolean(),
  }),
});

/** One figure three ways: before VAT, its VAT, and VAT included. */
const StatementSplit = z.object({
  excludingTaxMinor: MinorUnits,
  taxMinor: MinorUnits,
  includingTaxMinor: MinorUnits,
});

/**
 * How a calculated figure was reached, for the screen's hover: the operands
 * only (`StatementFormula`, order-money.ts).
 */
const StatementFormula = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('UNITS'), quantity: z.int(), unitMinor: MinorUnits }),
  z.object({ kind: z.literal('GAP'), guestPriceMinor: MinorUnits, bakerPriceMinor: MinorUnits }),
  z.object({ kind: z.literal('RATE'), baseMinor: MinorUnits, rateBps: z.int().nonnegative() }),
  z.object({
    kind: z.literal('SHARE'),
    wholeMinor: MinorUnits,
    weightMinor: MinorUnits,
    weightsMinor: MinorUnits,
  }),
  z.object({
    kind: z.literal('DELIVERY'),
    costMinor: MinorUnits,
    costFinal: z.boolean(),
    paidByGuestMinor: MinorUnits,
  }),
]);

/**
 * One table of the order's financial statement, laid out as that party's
 * invoice lays it out (`orderFinancialStatementOf`): rows and their total,
 * every figure handed over, nothing for the screen to compute.
 */
const StatementSection = z.object({
  rows: z.array(
    z.object({
      kind: z.enum([
        'PRODUCT',
        'PRODUCTS_SUBTOTAL',
        'DELIVERY',
        'SERVICE',
        'SMALL_ORDER',
        'FEE_DISCOUNT',
        'COMMISSION',
        'OWNER_COMMISSION',
        'DELIVERY_COST',
      ]),
      label: z.string().max(400).nullable(),
      quantity: z.int().nullable(),
      vatRateBps: z.int().nonnegative().nullable(),
      /** A set's products: each its share of the set's price, its own rate and VAT. */
      components: z
        .array(
          z.object({
            name: z.string().max(400),
            quantity: z.int().nonnegative(),
            vatRateBps: z.int().nonnegative(),
            formula: StatementFormula.nullable(),
            split: StatementSplit,
          }),
        )
        .nullable(),
      formula: StatementFormula.nullable(),
      split: StatementSplit,
    }),
  ),
  total: StatementSplit,
  /** After the total, one negative line per decided case that moved this table, with its reasons. */
  refunds: z.array(
    z.object({
      caseCode: z.int().min(1000),
      reasons: z.array(z.enum(SUPPORT_REASON_KINDS)),
      split: StatementSplit,
    }),
  ),
  /** The total with every refund line taken off: the total itself when there is none. */
  totalAfterRefund: StatementSplit,
});

// ---------------------------------------------------------------------------
// Every fact about one order, on its page (founder, 2026-10-26)
// ---------------------------------------------------------------------------

export const PaymentMovementStatus = z.enum([
  'PENDING',
  'SENT',
  'CONFIRMED',
  'FAILED',
  'NEEDS_HUMAN',
]);
export const PaymentMovementKind = z.enum(['REFUND', 'TRANSFER', 'REVERSAL', 'CANCEL_PAYMENT']);

/** One instruction the platform gave Stripe (Support > Payments, and the order's own page). */
export const PaymentMovementRow = z.object({
  id: z.string().min(1).max(80),
  kind: PaymentMovementKind,
  /** Who receives it (a refund: the guest; a reversal: who it is taken back from). */
  party: z.enum(['GUEST', 'BAKERY', 'PARTNER']),
  partyName: z.string().max(160).nullable(),
  orderId: Uuid,
  orderCode: z.string().max(16),
  caseId: Uuid.nullable(),
  caseCode: z.int().min(1000).nullable(),
  amountMinor: MinorUnitsNonNegative,
  currency: CurrencyCode,
  status: PaymentMovementStatus,
  /** The movement's own state; `status` reads a refund Stripe failed as NEEDS_HUMAN, which nothing retries. */
  state: PaymentMovementStatus,
  attempts: z.int().min(0),
  nextAttemptAt: Instant.nullable(),
  lastError: z.string().max(500).nullable(),
  stripeRef: z.string().max(120).nullable(),
  createdAt: Instant,
  updatedAt: Instant,
});
export type PaymentMovementRow = z.infer<typeof PaymentMovementRow>;

/** What the order owes one party, and what has moved towards it (`order_transfers`). */
export const OrderTransferRow = z.object({
  party: z.enum(['BAKERY', 'PARTNER', 'BAKERY_CHARGE']),
  partyName: z.string().max(160).nullable(),
  targetMinor: MinorUnits,
  transferredMinor: MinorUnitsNonNegative,
  reversedMinor: MinorUnitsNonNegative,
  state: z.enum(['OWED', 'SETTLED', 'FAILED']),
  updatedAt: Instant,
});
export type OrderTransferRow = z.infer<typeof OrderTransferRow>;

/** One of the order's Support cases, as its page lists it; the case page has the rest. */
export const OrderSupportCase = z.object({
  id: Uuid,
  code: z.int().min(1000),
  kind: z.enum(['ORDER', 'BANK']),
  status: z.enum(['OPEN', 'DECIDED']),
  reasons: z.array(z.enum(SUPPORT_REASON_KINDS)),
  openedAt: Instant,
  decidedAt: Instant.nullable(),
  decidedBy: z.string().max(160).nullable(),
  /** What Stripe ruled on a dispute; null while it has not, and on every other case. */
  bankOutcome: z.enum(['WON', 'LOST']).nullable(),
  /** The decided table's totals (`tableTotals`): what goes back to the guest and who pays. Null while open. */
  money: z
    .object({
      guestMinor: MinorUnitsNonNegative,
      bakeryMinor: MinorUnitsNonNegative,
      courierMinor: MinorUnitsNonNegative,
      croissantMinor: MinorUnitsNonNegative,
    })
    .nullable(),
});
export type OrderSupportCase = z.infer<typeof OrderSupportCase>;

/** The guest's issue report, whole: what the order's history carries only an excerpt of. */
export const OrderIssueReport = z.object({
  ticketRef: z.string().max(80).nullable(),
  at: Instant,
  text: z.string().max(2000),
  contactEmail: z.string().max(320).nullable(),
  /** The guest's photograph, a short-lived signed address; null when they sent none. */
  photoUrl: ImageUrl.nullable(),
  itemsFlagged: z.int().nonnegative(),
});
export type OrderIssueReport = z.infer<typeof OrderIssueReport>;

/** One rating the guest left after delivery (GST-038). */
export const OrderRating = z.object({
  subject: z.enum(['DELIVERY', 'PRODUCTS']),
  stars: z.int().min(1).max(5),
  comment: z.string().max(2000).nullable(),
  at: Instant,
});
export type OrderRating = z.infer<typeof OrderRating>;

/** What a provider's receipt said became of one sent message. */
export const NotificationReceiptState = z.enum([
  'SENT',
  'DELIVERED',
  'READ',
  'FAILED',
  'BOUNCED',
  'DELAYED',
  'COMPLAINED',
]);
export type NotificationReceiptState = z.infer<typeof NotificationReceiptState>;

/** The latest receipt for one message, keyed by the provider's message reference. */
export const NotificationReceipt = z.object({
  messageRef: z.string().max(200),
  state: NotificationReceiptState,
  at: Instant,
});
export type NotificationReceipt = z.infer<typeof NotificationReceipt>;

/** A message to the guest still in the outbox: deferred by quiet hours, or retrying. */
export const PendingNotification = z.object({
  id: z.string().max(80),
  messageType: z.string().max(80),
  channel: z.enum(['SMS', 'EMAIL', 'WHATSAPP']),
  templateKey: z.string().max(120),
  availableAt: Instant,
  attempts: z.int().nonnegative(),
});
export type PendingNotification = z.infer<typeof PendingNotification>;

export const getOrderDetail = defineEndpoint({
  method: 'GET',
  path: '/internal/orders/:orderId',
  mount: 'internal',
  summary: 'Full internal order view including the immutable allocation snapshot (PAY-003)',
  params: z.object({ orderId: Uuid }),
  response: z.object({
    row: DashboardRow,
    /**
     * THE COURIER SEARCH, while the platform is still asking (RTE-035, #1758):
     * when it began and the instant it gives up. Null when no search is in
     * progress - none started, a rider found, the job stood down by an
     * operator, the search stopped, or the order ended. Between two drops the
     * delivery reads NO_COURIER, and without this the page could only read
     * that as a courier that came to nothing.
     */
    courierSearch: z.object({ startedAt: Instant, deadline: Instant }).nullable(),
    allocation: AllocationSnapshot.nullable(),
    /**
     * The order's money as the guest's, the baker's, the owner's and
     * Croissant's invoices present it, each table's decided refunds stated
     * after its total as a correction of it (issue #2272).
     */
    financials: z.object({
      guest: StatementSection,
      baker: StatementSection,
      owner: StatementSection,
      croissant: StatementSection,
      /**
       * Who gets what of what the guest paid after refund, tips included: the
       * baker and Croissant before VAT and their VAT, the owner and the courier
       * as paid (`RevenueShare`, order-money.ts).
       */
      share: z.object({
        totalMinor: z.int(),
        bakerMinor: z.int(),
        bakerVatMinor: z.int(),
        croissantMinor: z.int(),
        croissantVatMinor: z.int(),
        ownerMinor: z.int(),
        deliveryMinor: z.int(),
      }),
    }),
    /** The open money question, and the claim chasing the provider (PAY-013). */
    claim: ClaimView,
    /**
     * What the guest was asked to pay: the order's total, VAT included,
     * whatever became of the payment - the 100% the financials card shares
     * out. NOT what the guest paid: a refused card or an abandoned 3-D Secure
     * leaves this at the full total with nothing taken (`guestPaid`).
     */
    guestTotal: MinorUnitsNonNegative,
    /**
     * What the guest actually paid for this order: its total once the card
     * was charged, zero while it never was (`orderMoneyView().grossCharged`,
     * the payment's own capture). The owed banner and the new-case form read
     * this, never `guestTotal`, so an unpaid order is owed nothing and no
     * refund of money never taken is prefilled (PAY-013, SUP-008).
     */
    guestPaid: MinorUnitsNonNegative,
    currency: CurrencyCode,
    price: PriceBreakdown,
    lines: z.array(OrderLine),
    refundedTotal: MinorUnitsNonNegative,
    /**
     * The cost of dearer stock-out replacements this order absorbed (GST-020):
     * a card authorization is never increased off-session, so the guest paid
     * nothing extra and the house handed over the difference at its own
     * original price (neither price goes up). Stated here, from the one money
     * view; nothing is settled for it.
     */
    substitutionAbsorbed: MinorUnitsNonNegative,
    /** The order's payment as the gateway reported it; null with no payment row. */
    payment: stripePaymentSchema.nullable(),
    /** The Stripe dashboard the payment id links under - live, or `/test`. */
    stripeDashboardUrl: z.string(),
    /** Splits the guest-cancellation row of the liability matrix (SUP-008). */
    bakeryAccepted: z.boolean(),
    /** The apartment this order came from and the conciergerie behind it. */
    parties: OrderParties,
    /**
     * THE ORDER CAMERA: what the counter said at Ready and the photographs of
     * the bag that have arrived, each a short-lived signed address - dispute
     * evidence. `outcome` null with no photo: the screen draws no card.
     */
    bagPhotos: BagPhotoRecord,
    events: z.array(
      z.object({
        id: Uuid,
        at: Instant,
        kind: z.string().max(80),
        actor: z.string().max(120),
        /**
         * The counter THIS event was about, named.
         *
         * A refusal does not carry the shop that refused - the shop was
         * implicit in the offer it answers - so the timeline could only say
         * "Refusee par la boulangerie, par Comptoir": the actor word twice and
         * a name nowhere. On a cascade that is not merely terse, it is the
         * whole question, because the second refusal is a DIFFERENT house from
         * the first and nothing on the screen could tell them apart.
         *
         * Null for the events that concern no counter - payment, delivery, the
         * order's own ending - rather than borrowing the order's current shop,
         * which is what put one name on all eleven rows.
         */
        counterName: z.string().max(160).nullable(),
        /**
         * How loud this line is, derived by the SAME rule the Activity feed
         * uses (`severityOfOrderEvent`, `severityOfAlert`).
         *
         * On the wire rather than in the screen because it is a read model and
         * this codebase pays for every second implementation of one: the feed
         * already derives it server-side, and a copy in the client would be
         * two answers to "is this row serious" that part company the first time
         * either moves. The timeline used to have no answer at all for a plain
         * order event - only alerts carried one - so every transition drew the
         * same, and a cancellation read exactly like a payment starting.
         */
        severity: ActivitySeverity,
        payload: z.record(z.string(), z.unknown()),
        /**
         * What the guest paid, on the line where the money moved.
         *
         * Non-null on `PAYMENT_CAPTURED` and on nothing else: it is derived
         * from the order rather than carried by the event, so putting it on
         * every row would be the same figures repeated twenty times down one
         * card. Beside `payload` rather than folded into it, because `payload`
         * is the producer's own record and this is a read model - blurring the
         * two is how a derivation ends up looking like something that was
         * written at the time.
         */
        payment: OrderPaymentSummary.nullable(),
        /**
         * THE TRANSITION ITSELF: which region this row moved, and where that
         * region stood either side of it (founder, 2026-09-08: "make sure that
         * all state machine states and transitions are rendered visible in the
         * order activity detailed log").
         *
         * Every `order_events` row has carried all three since the regions
         * were written, and the Activity feed has spread them into its
         * `detail` from the start - so a feed row read "delivery ESTIMATED ->
         * QUOTED" while the order's OWN screen, the one built to tell that
         * order's story, printed the event's title and never once named a
         * state. `fromState === toState` is an annotation rather than a move
         * (a support reply, a sold-out line): the row still says which region
         * it was about.
         */
        axis: EventAxisName,
        fromState: z.string().max(40),
        toState: z.string().max(40),
        /**
         * THE OTHER REGIONS THE SAME EVENT MOVED, on the same row.
         *
         * A row names the region its event OWNS, and a second row for the
         * second region is how one cancellation came to be stated twice
         * (R2-D2). But a declared cross-region move is a real move: the
         * courier's handover collects the bag, so `PROVIDER_PICKED_UP` carries
         * `order READY -> COLLECTED` with it. Round 1 of the live loop read
         * three delivered orders whose logs never said the order reached
         * COLLECTED or DELIVERED at all, and then closed them `DELIVERED ->
         * CLOSED` off a position nothing had claimed (R1-D1).
         *
         * Optional on the wire because the worker deploys before the screens
         * and because every row written before this carries none - an
         * append-only log is not rewritten to add a field.
         */
        alsoMoved: z
          .array(
            z.object({
              axis: EventAxisName,
              fromState: z.string().max(40),
              toState: z.string().max(40),
            }),
          )
          .optional(),
      }),
    ),
    /**
     * The counter answerable for an order nobody made (ALC-006).
     *
     * The FIRST shop that was offered this order and refused it - the one that
     * held it from checkout and then handed it back, typically by recording a
     * closure over a morning it had already sold. A cascade candidate that
     * declines an offer it never accepted has broken no promise and is not
     * named here.
     *
     * Null unless somebody refused: an order for which no counter was ever
     * eligible has nobody to answer for it.
     */
    answerableCounter: z
      .object({ bakeryLocationId: Uuid, name: z.string().max(160).nullable() })
      .nullable(),
    /** The order's SupportConversation, oldest first (SUP-006, GST-039). */
    support: z.object({ messages: z.array(SupportMessage) }),
    /** The guest's issue reports, whole - full text, contact address, photograph. */
    issueReports: z.array(OrderIssueReport),
    /** The guest's ratings after delivery (GST-038). */
    ratings: z.array(OrderRating),
    /** The order's Support cases, oldest first. */
    cases: z.array(OrderSupportCase),
    /** Every instruction given Stripe for this order: refunds, transfers, reversals. */
    movements: z.array(PaymentMovementRow),
    /** What the order owes the house and the owner, and what has moved. */
    transfers: z.array(OrderTransferRow),
    /** The latest provider receipt of each message sent about the order. */
    notificationReceipts: z.array(NotificationReceipt),
    /** Messages to the guest still waiting in the outbox. */
    pendingNotifications: z.array(PendingNotification),
    /** The courier's proof of delivery (photo, signature or PIN); null without one. */
    proofOfDelivery: ProofOfDelivery.nullable(),
    /**
     * The houses, shops and apartments the timeline's payloads mention, named.
     *
     * An order that cascaded prints one OFFERED event per house it was put to,
     * and every one of those payloads carries `bakeryOrgId` and
     * `bakeryLocationId` and nothing else about the place - so the answer to
     * "who refused this?" was three uuids in a row.
     */
    names: PayloadNames,
  }),
});

/**
 * SUP-006 / GST-039: answer the guest's issue report.
 *
 * The guest half promises "we will come back to you"; this is the only control
 * that can keep it. The reply is appended to the order's SupportConversation,
 * audited (OPS-008), and sent OUT through the Notifier - SMS, plus email when
 * checkout captured one - because guest SMS is one-way (SUP-009) and the order
 * page the message links to is where the guest reads the thread.
 */
export const replySupportConversation = defineEndpoint({
  method: 'POST',
  path: '/internal/orders/:orderId/support/reply',
  mount: 'internal',
  summary: "Reply to the guest's issue report; recorded on the SupportConversation (SUP-006)",
  params: z.object({ orderId: Uuid }),
  body: z.object({ message: z.string().min(1).max(1000) }),
  idempotent: true,
  response: z.object({ messages: z.array(SupportMessage) }),
});

// ---------------------------------------------------------------------------
// The activity feed (PRD OPS-001)
// ---------------------------------------------------------------------------

/**
 * Which slice of the platform's history an item came from.
 *
 * The internal portal has always had five separate lists - Alerts, Orders,
 * Audit, Dead letter, and an order's own events - with no join between them, so
 * reconstructing a morning meant opening five screens and comparing timestamps
 * by eye. Worse, the largest slice had no screen at all: every guest attempt
 * the platform refused BEFORE an order existed was persisted nowhere, so the
 * feed's own denominator was missing.
 *
 * One kind per SOURCE rather than per subject, because that is what decides how
 * an item is fetched and how far back it can be asked for. A screen filters on
 * these; it never has to know which table each one came from.
 */
export const ActivityKind = z.enum([
  'ALERT',
  'ORDER_EVENT',
  'REFUSAL',
  'AUDIT',
  'DEAD_LETTER',
  /**
   * A message the platform rendered, and what became of it (TST-002).
   *
   * A sixth source, because the other five could not answer the question an
   * operator actually asks - "did the guest get told?". A message that failed
   * reached the feed only as a DEAD_LETTER, beside nothing; a message that was
   * held back by `NOTIFY_ALLOWLIST` reached it not at all; and a message that
   * really went out was provable only in Twilio's or Resend's own dashboard.
   */
  'NOTIFICATION',
]);
export type ActivityKind = z.infer<typeof ActivityKind>;

/**
 * One thing that happened, in the one shape every source is projected into.
 *
 * `title` is a CODE and never a sentence - the alert kind, the order event
 * type, the refusal cause, the audit action, the dead-letter message type. The
 * screen translates it; the wire does not, because the same feed is read in two
 * languages and a server-rendered sentence would be readable in exactly one of
 * them. `detail` carries the producer's own fields verbatim, exactly as the
 * alerts table already renders an alert payload: a feed that summarised would
 * be a feed that loses the fact somebody opened it for.
 */

export const ActivityItem = z
  .object({
    /**
     * Stable per item, and NOT a uuid: an alert's identity is its dedupe key and
     * an audit row's is its sequence. The feed only needs it to be unique and to
     * survive a refetch, which is what makes a cursor and a React key work.
     */
    id: z.string().min(1).max(200),
    at: Instant,
    kind: ActivityKind,
    /**
     * How loudly to render it. Derived by the projector from the item's own
     * source and code - never stored - so a new alert kind or a new refusal cause
     * lands somewhere sensible instead of nowhere.
     */
    severity: ActivitySeverity,
    title: z.string().max(160),
    orderId: Uuid.nullable(),
    orderShortCode: z.string().max(10).nullable(),
    bakeryLocationId: Uuid.nullable(),
    bakeryName: z.string().max(160).nullable(),
    /**
     * The apartment as an operator names it - `property.name` - and never its QR
     * token: the token is a bearer credential, and a feed is the last place to
     * print one. Truncated rather than rejected past the cap, because one long
     * name must not fail the whole page.
     */
    propertyReference: z.string().max(120).nullable(),
    /**
     * Who acted, when somebody did.
     *
     * An operator's verified email address on `/internal`, an account id where the
     * acceptor could not resolve one, and a role or a device elsewhere. It was an
     * account id everywhere until an operator renaming a bakery showed up on the
     * Activity feed as "Operator 3HlTtCgg" - a row nothing on any screen could
     * turn back into a person. NFR-009 was cited here for years as the reason; it
     * is about BUILDING ACCESS CODES and says nothing about naming staff.
     */
    actor: z.string().max(160).nullable(),
    /**
     * What the item is ABOUT, named rather than identified.
     *
     * An audit row's subject is a `(type, id)` pair on the stored row, and it
     * used to reach the screen only by falling through into `detail` - so the
     * one fact a reader opened a pairing row for, WHICH SHOP, was rendered as a
     * bare uuid beside `subjectType bakery_location`. The projector resolves the
     * name here instead, through the same batched read the feed already does for
     * apartments and counters, and the screen prints the label.
     *
     * `label` is null when the type has no cheap name - a rate or a kill
     * switch - and the screen then falls back to naming the TYPE, which is
     * still a sentence rather than a uuid. The id travels for the reader who
     * needs to correlate it, and belongs behind the row rather than on it.
     */
    subject: z
      .object({
        type: z.string().max(40),
        id: z.string().max(80),
        label: z.string().max(160).nullable(),
      })
      .nullable(),
    detail: z.record(z.string(), z.unknown()),
  })
  .superRefine((item, ctx) => {
    /*
     * An ALERT's title is its kind, so it is a member of the closed list: the
     * screen titles every member and shows the code beside the title, and has no
     * fallback for a code it was never told about.
     */
    if (item.kind === 'ALERT' && !InternalAlertKind.safeParse(item.title).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['title'],
        message: `alert kind "${item.title}" is not in INTERNAL_ALERT_KINDS`,
      });
    }
  });
export type ActivityItem = z.infer<typeof ActivityItem>;

export const listActivity = defineEndpoint({
  method: 'GET',
  path: '/internal/activity',
  mount: 'internal',
  summary: 'One feed over alerts, order events, refusals, audit and dead letters (OPS-001)',
  query: z.object({
    /*
     * A SET of kinds, repeated on the wire (`?kinds=ALERT&kinds=REFUSAL`) -
     * `bind.ts` binds an array-typed query key from every occurrence of it,
     * the same convention `listOrganizations.type` uses. Omitted means every
     * kind. Filtering server-side rather than client-side for the reason
     * stated there: `limit` is applied before anything a client could drop.
     */
    kinds: z.array(ActivityKind).min(1).optional(),
    /** Any of these severities, repeated on the wire; omitted or empty means every one. */
    severity: z.array(ActivitySeverity).max(ActivitySeverity.options.length).optional(),
    orderId: Uuid.optional(),
    /** Any of these shops, repeated on the wire; omitted or empty means every shop. */
    bakeryLocationId: z.array(Uuid).max(200).optional(),
    since: Instant.optional(),
    until: Instant.optional(),
    /**
     * The last item's position, `<millis>|<id>` - the feed pages by `(at DESC,
     * id DESC)` rather than by an offset, because the sources keep producing
     * while somebody is reading and an offset silently skips or repeats rows as
     * they do. Long enough to hold an item id, which is a producer's dedupe key
     * rather than a uuid.
     */
    cursor: z.string().max(300).optional(),
    limit: z.int().positive().max(200).default(50),
  }),
  response: z.object({
    items: z.array(ActivityItem),
    nextCursor: z.string().max(300).nullable(),
    names: PayloadNames,
    /**
     * Which severities and which shops the feed's two multi-select filters can
     * offer, and how many items carry each (founder, 2026-09-14). Counted over
     * the whole window the kinds, the range and the order select - identical
     * on every page, because the cursor is applied after - and each filter with
     * the OTHER one applied and its own ignored (`facetedFilter`).
     */
    facets: z.object({
      severity: z.array(FilterFacet),
      bakery: z.array(FilterFacet),
    }),
  }),
});

/**
 * A service day in five numbers, for the tile above the feed (OPS-001).
 *
 * `refused` is a map rather than a total because the total is the least useful
 * form of it: "eleven refusals" is a shrug, "nine NO_BAKERY" is a phone call.
 * `declined` is broken out of that map deliberately - a declined card is the
 * guest's problem and the rest are the platform's, and mixing them makes a bad
 * morning look like a payments incident.
 */
export const ActivitySummary = z.object({
  serviceDay: ServiceDay,
  placed: z.int().nonnegative(),
  delivered: z.int().nonnegative(),
  cancelled: z.int().nonnegative(),
  /**
   * Cause -> count, for the day. Causes with no refusals are ABSENT, which is
   * why this is `partialRecord` and not `record`: a record keyed by an enum
   * demands every member be present, so a clean morning - the normal case -
   * would fail its own contract and answer 500. `RESPONSE_VALIDATION` caught
   * exactly that on the first run of this endpoint.
   */
  refused: z.partialRecord(CheckoutRefusalCause, z.int().nonnegative()),
  /** PAYMENT_DECLINED, counted on its own as well as inside `refused`. */
  declined: z.int().nonnegative(),
  openAlerts: z.int().nonnegative(),
});
export type ActivitySummary = z.infer<typeof ActivitySummary>;

export const getActivitySummary = defineEndpoint({
  method: 'GET',
  path: '/internal/activity/summary',
  mount: 'internal',
  summary: "A service day's orders, refusals and open alerts in one tile (OPS-001)",
  query: z.object({ serviceDay: ServiceDay.optional() }),
  response: ActivitySummary,
});

export const listOpsAlerts = defineEndpoint({
  method: 'GET',
  path: '/internal/alerts',
  mount: 'internal',
  summary:
    'Operations alerts raised by the platform, newest first (OPS-011, BAK-020, SUP-003, NTF-003)',
  query: z.object({
    kind: z.string().max(80).optional(),
    orderId: Uuid.optional(),
    bakeryLocationId: Uuid.optional(),
    limit: z.int().positive().max(200).default(50),
  }),
  response: z.object({ alerts: z.array(OperationsAlert) }),
});

// ---------------------------------------------------------------------------
// Manual controls, one order at a time (PRD OPS-002)
// ---------------------------------------------------------------------------

export const reassignBakery = defineEndpoint({
  method: 'POST',
  path: '/internal/orders/:orderId/reassign',
  mount: 'internal',
  summary: 'Reassign the fulfilling bakery; forbidden for a guest-selected shop (OPS-002, ALC-010)',
  params: z.object({ orderId: Uuid }),
  body: z.object({
    /*
     * No distance override: there is no per-shop service radius to move an
     * order past (founder, 2026-09-24). A stale client still sending
     * `overrideReach` has it stripped, as any unknown key is.
     */
    bakeryLocationId: Uuid,
  }),
  idempotent: true,
  response: Ok,
});

/**
 * The four manual moves an operator can make on a provider job (OPS-002,
 * SUP-002, SUP-003), in the order the order record offers them.
 */
export const DeliveryActionName = z.enum(['DISPATCH_NOW', 'DELAY_PICKUP', 'CANCEL_JOB', 'REBOOK']);
export type DeliveryActionName = z.infer<typeof DeliveryActionName>;

/**
 * OPS-002 / SUP-003 - the number an operator's delay field OPENS on, and
 * nothing else (founder, 6 October 2026, centralization audit K12-6: "default
 * should be 30mn, it should not be possible to do anything without seeing the
 * number").
 *
 * It is the pre-fill of every field that asks how far to move a pickup - the
 * Orders row's Delay dialog and the order page's delivery panel (Delay pickup,
 * Re-book) - which the operator reads and
 * may change before confirming. It is NOT a server default: a delay that
 * arrives without its minutes is refused by the contract below
 * (`OperatorDelayMinutes` is required on every delaying arm), never filled in.
 * Reading this constant anywhere but a screen's initial field value is how a
 * delay nobody saw comes back.
 */
export const DEFAULT_OPERATOR_DELAY_MINUTES = 30;

/** How far an operator may move a pickup in one go, in whole minutes (OPS-002). */
export const OperatorDelayMinutes = z.int().min(1).max(240);

/**
 * The delivery controls that move the pickup by a number of minutes, and so
 * REQUIRE it (K12-6): `DELAY_PICKUP` shifts the promise by it, and `REBOOK`
 * books the replacement courier that many minutes out, moving the promise as
 * far as that lead time needs.
 */
export const DelayingDeliveryAction = DeliveryActionName.extract(['DELAY_PICKUP', 'REBOOK']);
export type DelayingDeliveryAction = z.infer<typeof DelayingDeliveryAction>;

export const rebookDelivery = defineEndpoint({
  method: 'POST',
  path: '/internal/orders/:orderId/delivery',
  mount: 'internal',
  summary:
    'Dispatch now, delay pickup, cancel or re-book the provider job (OPS-002, SUP-002, SUP-003)',
  params: z.object({ orderId: Uuid }),
  /*
   * Discriminated on the action, so the minutes are required exactly where they
   * move something and a delay without them is a 400 rather than a number the
   * server picked (K12-6). The other two take none.
   */
  body: z.discriminatedUnion('action', [
    z.object({ action: DelayingDeliveryAction, delayMinutes: OperatorDelayMinutes }),
    z.object({ action: DeliveryActionName.exclude(['DELAY_PICKUP', 'REBOOK']) }),
  ]),
  idempotent: true,
  response: z.object({ delivery: DeliveryJobSummary.nullable() }),
});

/**
 * AN OPERATOR ENDS A COURIER JOB NO PROVIDER ENDED (issue #1471, plan
 * `cancellation-settles-and-tells.html` R4).
 *
 * Legal only on an order that has ended while its delivery has not settled
 * (`deliveryClosableByOperations`, the domain's `mayCloseDeliveryByOperations`):
 * the operator states which settled state the job reached and why, and the
 * order's refund case is released like any other settlement. Nothing else about
 * the order moves.
 */
export const closeDelivery = defineEndpoint({
  method: 'POST',
  path: '/internal/orders/:orderId/delivery/close',
  mount: 'internal',
  summary: 'Close the courier job of an ended order the provider never closed (issue #1471)',
  params: z.object({ orderId: Uuid }),
  body: z.object({
    outcome: DeliveryClosedOutcome,
    /** Required: what the operator knows that the provider never said. */
    note: z.string().trim().min(1).max(500),
  }),
  idempotent: true,
  response: Ok,
});

export const moveClaim = defineEndpoint({
  method: 'POST',
  path: '/internal/orders/:orderId/claim',
  mount: 'internal',
  summary: 'Settle, reject, escalate or write off the provider claim (PAY-013, PAY-030)',
  params: z.object({ orderId: Uuid }),
  body: z.object({
    action: ClaimAction,
    /** What the provider actually paid, on SETTLE. */
    amount: MinorUnitsNonNegative.nullable().default(null),
    /** Required for REJECT, ESCALATE and WRITE_OFF: the provider's words. */
    reason: z.string().max(500).nullable().default(null),
  }),
  idempotent: true,
  response: z.object({ claim: ClaimView }),
});

/**
 * The operator's cancel of ONE order (OPS-002, SUP-008): Internal acts on one
 * record at a time (founder, 14 October 2026), so this is the Orders row
 * menu's Cancel and the support cancel.
 *
 * A cancellation is free-standing (founder decision 8) and moves no money: the
 * cancelled order is final, and ONE Support case opens behind it when something
 * is owed (issue #2272; docs/finance/refunds-and-disputes.html). Refused 409
 * `cancellation_closed` once the bag reached the guest (decision 11,
 * `operatorMayCancel`).
 */
export const cancelOrder = defineEndpoint({
  method: 'POST',
  path: '/internal/orders/:orderId/cancel',
  mount: 'internal',
  summary: 'Cancel one order; its Support case opens when it is final (OPS-002, SUP-008)',
  params: z.object({ orderId: Uuid }),
  body: z.object({}),
  idempotent: true,
  response: z.object({
    /**
     * The provider refused the job cancel while a rider was coming, so one may
     * still arrive and somebody has to chase it (issue #1471, R5).
     */
    courierNotStoodDown: z.boolean(),
  }),
});
